/**
 * API: Klaviyo
 * -----------------------------------------------------------------------------
 * Provides a wrapper for the Klaviyo API.
 * - https://developers.klaviyo.com/en/reference/api-overview
 *
 * Payload must match the structure outlined in the link below:
 * - https://developers.klaviyo.com/en/reference/create_client_subscription
 */

/**
 * Subscribes a new email address to Klaviyo.
 * - {data} can only include the properties `type` and `attributes`.
 *
 * @param {Object} data - Form data object.
 * @param {String} type - Type of subscribe method.
 * @returns {Promise}
 */
export default function subscribe(data = {}, type) {
  const key = cnvs?.credentials?.klaviyo?.publicKey
  let url
  let payload

  if (!data || !key) {
    return Promise.reject(
      new Error('Email address or Klaviyo credentials not set.'),
    )
  }

  switch (type) {
    case 'bis':
      url = `//a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${key}`
      payload = {
        headers: {
          accept: 'application/json',
          revision: '2024-06-15',
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: data.email,
                  },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${data.variantId}`,
                },
              },
            },
          },
        }),
        method: 'POST',
      }
      break
    default:
      url = `https://a.klaviyo.com/client/subscriptions/?company_id=${key}`
      payload = {

        /**
         * Must use JSON.stringify() because of strict Klaviyo requirements.
         */
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: data.custom_source,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: data.email,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: data.list_id,
                },
              },
            },
          },
        }),
        headers: {
          revision: '2024-06-15',
          'content-type': 'application/json',
        },
        method: 'POST',
      }
      break
  }

  return new Promise((resolve, reject) => {
    fetch(
      url,
      payload,
    )

      /**
       * Using response.text() as response.json() resulted in a SyntaxError due
       * to 'Unexpected end of JSON'.
       */
      .then(response => response.text())
      .then(response => resolve(response))
      .catch(
        error => {
          reportError('Klaviyo API request failed', error)
          reject(error)
        },
      )
  })
}
